import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from "axios"
import { toast } from "sonner"
import { getAccessToken, isAccessTokenExpired, renovarToken } from "./auth"
import { useSessionStore } from "@/stores/sessionStore"

/**
 * Cliente HTTP com:
 * - Injeção de Authorization (Bearer) do access token em memória
 * - 401 → refresh silencioso via /auth/refresh (cookie HttpOnly) + retry único
 * - 403 → toast de permissão (UX; segurança real no backend)
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 30_000,
  headers: { Accept: "application/json" },
})

async function ensureFreshToken(): Promise<boolean> {
  // O cliente de negócio é exclusivo de sessões internas. Token de cidadão
  // gov.br nunca é enviado para APIs internas (o backend também bloqueia).
  const { tipo } = useSessionStore.getState()
  if (tipo !== "INTERNO") return false
  if (getAccessToken() && !isAccessTokenExpired()) {
    return true
  }
  // renovarToken é single-flight em lib/auth — todos os call sites compartilham
  // a mesma requisição (o Keycloak rotaciona o refresh token a cada uso).
  return renovarToken("interno")
}

apiClient.interceptors.request.use(async (config) => {
  config.headers.set("X-Request-Id", crypto.randomUUID())
  // Grupo ativo (escopo de tenancy): nome do grupo em atuação; ausente = escopo legado.
  const { grupoAtivo } = useSessionStore.getState()
  if (grupoAtivo) {
    config.headers.set("X-Grupo", grupoAtivo.nome)
  }
  const ok = await ensureFreshToken()
  const token = getAccessToken()
  if (ok && token) {
    config.headers.set("Authorization", `Bearer ${token}`)
  }
  return config
})

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retried?: boolean }
    const status = error.response?.status

    const { tipo } = useSessionStore.getState()

    // 401 → tenta refresh uma única vez e re-executa a requisição original
    // (somente para sessão interna; cidadão usa os endpoints públicos).
    if (status === 401 && original && !original._retried && tipo === "INTERNO") {
      original._retried = true
      const ok = await ensureFreshToken()
      if (ok && getAccessToken()) {
        original.headers.set("Authorization", `Bearer ${getAccessToken()}`)
        return apiClient(original)
      }
      toast.error("Sessão expirada. Entre novamente.")
      window.location.href = "/login"
      return Promise.reject(error)
    }

    if (status === 403) {
      toast.error("Você não tem permissão para realizar esta ação.")
    }
    return Promise.reject(error)
  },
)

/** Extrai a mensagem amigável de um erro (ProblemDetail RFC 9457). */
export function errorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data as { detail?: string; title?: string } | undefined
    if (detail?.detail) return detail.detail
    if (detail?.title) return detail.title
    return error.message
  }
  return "Erro inesperado."
}